import * as amplitude from '@amplitude/analytics-browser';
import { VpEditIcon, VpInformationIcon } from '@vtmn-play/icons/react';
import { VpButton, VpDivider, VpIcon } from '@vtmn-play/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRoles } from '../../../../contexts/RolesContext';
import DisplaySegmentations from './DisplaySegmentations';

import { SpoNatureFullResponse } from '@spo/types';
import '../drawer-nature.scss';
import './drawer-tab-settings.scss';

export const DrawerTabSettings = ({
  nature,
  setOpenDrawerAddSettings,
  editExistingSettings,
}: {
  nature: SpoNatureFullResponse;
  setOpenDrawerAddSettings: (open: boolean) => void;
  editExistingSettings: () => void;
}) => {
  const { t } = useTranslation();
  const { hasRoleSportLeader } = useRoles();

  const sortedSettings = useMemo(() => {
    return nature.settings.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [nature]);

  return nature.settings.length > 0 ? (
    <div
      className="drawer-tab-settings-container"
      data-testid="drawer-tab-settings-container"
    >
      <div className="archi-settings-warning-banner">
        <VpInformationIcon />
        {t('architecture.drawer.settings.warning-banner')}
      </div>
      <div className="settings-labels">
        {t('input.user-segmentation_label')}
      </div>
      <div>{nature.userSegmentationType.label}</div>
      <div className="settings-labels">
        {t('architecture.drawer.settings.segmentation-label')}
        {hasRoleSportLeader && (
          <VpButton
            shape="squared"
            size="small"
            startSlot={<VpEditIcon />}
            onClick={editExistingSettings}
            data-testid="drawer-modify-config-button"
          >
            {t('modify')}
          </VpButton>
        )}
      </div>
      <DisplaySegmentations settings={sortedSettings} />
      <VpDivider id="divider-label-id" className="settings-divider" />
      <div className="settings-labels">
        {t('architecture.drawer.settings.benefits-label')}
      </div>
      {sortedSettings[0].productBenefits.map((benefit) => (
        <div
          className="settings-benefits"
          key={benefit.id}
          data-testid={`settings-benefit-${benefit.id}`}
        >
          <div>{benefit.shortTechnicalLabel}</div>
          <p>{benefit.longTechnicalLabel}</p>
        </div>
      ))}
    </div>
  ) : (
    <div className="drawer-empty-config">
      {t('architecture.drawer.empty-configuration')}
      {hasRoleSportLeader && (
        <VpButton
          data-testid="drawer-add-config-button"
          shape="squared"
          onClick={() => {
            setOpenDrawerAddSettings(true);
            amplitude.track('Architecture_Drawer_Add-Settings');
          }}
        >
          <VpIcon name="add" />
          {t('architecture.drawer.settings.add')}
        </VpButton>
      )}
    </div>
  );
};
